/**
 * Agent Events API types (align with backend DTOs).
 */

export enum MetricGrouping {
  QUEUE = 'QUEUE',
  ROUTING_PROFILE = 'ROUTING_PROFILE',
  AGENT = 'AGENT',
  CHANNEL = 'CHANNEL',
  AGENT_HIERARCHY_LEVEL_ONE = 'AGENT_HIERARCHY_LEVEL_ONE',
  AGENT_HIERARCHY_LEVEL_TWO = 'AGENT_HIERARCHY_LEVEL_TWO',
  AGENT_HIERARCHY_LEVEL_THREE = 'AGENT_HIERARCHY_LEVEL_THREE',
  AGENT_HIERARCHY_LEVEL_FOUR = 'AGENT_HIERARCHY_LEVEL_FOUR',
  AGENT_HIERARCHY_LEVEL_FIVE = 'AGENT_HIERARCHY_LEVEL_FIVE',
}

export enum MetricIntervalPeriod {
  FIFTEEN_MIN = 'FIFTEEN_MIN',
  THIRTY_MIN = 'THIRTY_MIN',
  HOUR = 'HOUR',
  DAY = 'DAY',
  WEEK = 'WEEK',
  TOTAL = 'TOTAL',
}

export interface AgentQueueStatusDTO {
  agentId?: string;
  queueId: string;
  queueName: string;
  channel: string;
  waitingCount: number;
  oldestWaitTime: string;
}

export interface GetAgentQueueStatusOutputDTO {
  data: AgentQueueStatusDTO[];
}

export interface GetAgentQueueStatusParams {
  agentId?: string;
  agentIds?: string;
}

export interface RoutingProfileLiveCallsDTO {
  routingProfileId: string;
  routingProfileName: string;
  channel: string;
  liveCallCount: number;
}
export interface GetLiveCallsByRoutingProfileOutputDTO {
  data: RoutingProfileLiveCallsDTO[];
  totalCalls: number;
}
export interface GetLiveCallsByRoutingProfileParams {
  routingProfileId?: string;
}

export const SELECTABLE_INTERVAL_PERIODS: MetricIntervalPeriod[] = [
  MetricIntervalPeriod.HOUR,
  MetricIntervalPeriod.DAY,
  MetricIntervalPeriod.WEEK,
  MetricIntervalPeriod.TOTAL,
];

/** Occupancy endpoint supports these groupings only (QUEUE/CHANNEL ignored). */
export const OCCUPANCY_GROUPINGS: MetricGrouping[] = [
  MetricGrouping.ROUTING_PROFILE,
  MetricGrouping.AGENT,
  MetricGrouping.AGENT_HIERARCHY_LEVEL_ONE,
  MetricGrouping.AGENT_HIERARCHY_LEVEL_TWO,
  MetricGrouping.AGENT_HIERARCHY_LEVEL_THREE,
  MetricGrouping.AGENT_HIERARCHY_LEVEL_FOUR,
  MetricGrouping.AGENT_HIERARCHY_LEVEL_FIVE,
];

/** Adherence endpoint supports QUEUE, ROUTING_PROFILE, AGENT, CHANNEL. */
export const ADHERENCE_GROUPINGS: MetricGrouping[] = [
  MetricGrouping.QUEUE,
  MetricGrouping.ROUTING_PROFILE,
  MetricGrouping.AGENT,
  MetricGrouping.CHANNEL,
];

export interface MetricItemDTO {
  id: string;
  name: string;
  value: number | string;
  dimensions?: Record<string, string>;
  dimensionLabels?: Record<string, string>;
  intervalStartTime?: string;
  intervalEndTime?: string;
}

export interface MetricsOutputDTO {
  data: MetricItemDTO[];
}

export interface GetOccupancyMetricsParams {
  startTime?: string;
  endTime?: string;
  groupings?: MetricGrouping[];
  intervalPeriod?: MetricIntervalPeriod;
  timeZone?: string;
}

/** All metrics endpoints support the same parameters as occupancy. */
export type MetricsParams = GetOccupancyMetricsParams;

/** Valid targetSeconds for GET /agent-events/service-level. */
export const VALID_SERVICE_LEVEL_TARGETS = [
  15, 20, 25, 30, 45, 60, 90, 120, 180, 240, 300, 600,
] as const;

export type ServiceLevelTargetSeconds =
  (typeof VALID_SERVICE_LEVEL_TARGETS)[number];
export interface GetServiceLevelMetricsParams extends GetOccupancyMetricsParams {
  targetSeconds: ServiceLevelTargetSeconds;
}

/**
 * Agent Performance Dashboard API response
 * GET /agent-events/agent/dashboard
 * Returns aggregated metrics for agent's personal performance
 */

export interface MetricItemDTO {
  id: string;
  name: string;
  value: string | number; // HH:MM:SS format or percentage like "71.43%"
  dimensions?: Record<string, string>;
  dimensionLabels?: Record<string, string>;
  intervalStartTime?: string; // ISO 8601 date
  intervalEndTime?: string; // ISO 8601 date
}

export interface MetricsResponseDTO {
  data: MetricItemDTO[];
}

export interface AgentDashboardDTO {
  queueStatus: MetricsResponseDTO;
  averageWaitTime: MetricsResponseDTO;
  serviceLevel: MetricsResponseDTO;
  averageTalkTime: MetricsResponseDTO;
  averageHoldTime: MetricsResponseDTO;
}

export interface GetAgentDashboardParams {
  startTime?: string;
  endTime?: string;
  intervalPeriod?: MetricIntervalPeriod;
  targetSeconds?: ServiceLevelTargetSeconds;
  timeZone?: string;
}
